import React, { useEffect, useState } from 'react';
import { Moon, Sun, Monitor, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { getTheme, setTheme, type ThemeMode } from '@/lib/theme';

interface Props {
  className?: string;
  /** Compact icon-only trigger for tight headers (default true). */
  iconOnly?: boolean;
}

const ThemeToggle: React.FC<Props> = ({ className, iconOnly = true }) => {
  const [mode, setMode] = useState<ThemeMode>('system');

  useEffect(() => {
    setMode(getTheme());
    const onChange = (e: Event) =>
      setMode(((e as CustomEvent).detail as ThemeMode) ?? getTheme());
    window.addEventListener('resonow:theme', onChange);
    return () => window.removeEventListener('resonow:theme', onChange);
  }, []);

  const pick = (m: ThemeMode) => {
    setTheme(m);
    setMode(m);
  };

  const Icon = mode === 'dark' ? Moon : mode === 'light' ? Sun : Monitor;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Toggle theme"
          title="Theme"
          className={`inline-flex items-center gap-2 rounded-full border border-warm-200 dark:border-white/10 bg-white dark:bg-white/5 px-2.5 py-1.5 text-sm text-warm-700 dark:text-[hsl(var(--foreground))] hover:bg-warm-50 dark:hover:bg-white/10 transition-colors min-h-[36px] ${className ?? ''}`}
        >
          <Icon className="h-4 w-4" />
          {!iconOnly && (
            <span className="hidden sm:inline">
              {mode === 'dark' ? 'Dark' : mode === 'light' ? 'Light' : 'System'}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {(['light', 'dark', 'system'] as ThemeMode[]).map((m) => {
          const MIcon = m === 'dark' ? Moon : m === 'light' ? Sun : Monitor;
          const label = m === 'dark' ? 'Dark' : m === 'light' ? 'Light' : 'System';
          return (
            <DropdownMenuItem key={m} onClick={() => pick(m)} className="cursor-pointer">
              <MIcon className="h-4 w-4 mr-2" />
              <span className="flex-1">{label}</span>
              {mode === m && <Check className="h-4 w-4 opacity-70" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ThemeToggle;
